/* eslint-disable prettier/prettier */

import { useQuery } from '@tanstack/react-query';
import { formatEther } from 'viem';

import { queryOptions } from '~components/hooks/_constants';
import { useContractFeeTracker, useContractVaultRewards } from '~components/hooks/contracts';
import { useSession } from '~components/hooks/useSession';
import { checkValidContractAddress } from '~config/protocol';
import { EVestaAsset } from '~config/types';

import useAssetPrices from '../assets/useAssetPrices';

import { useGMXBonusAPR } from './useGMXBonus';

const RewardAPRQueryKey = 'vault-apr';

export const useVaultRewardAPR = (asset: EVestaAsset) => {
  const computedAsset = asset !== EVestaAsset.GMX && asset !== EVestaAsset.sGLP ? EVestaAsset.GMX : asset;

  const { address, network } = useSession();
  const { data: prices, isFetched: pricesFetched, isLoading: isPricesLoading } = useAssetPrices();
  const { read: feeTrackerRead, address: feeTrackerAddress } = useContractFeeTracker(computedAsset);
  const { read: vaultRewardsRead, address: vaultRewardsAddress } = useContractVaultRewards(EVestaAsset.GMX);
  const bonusApr = useGMXBonusAPR();

  const res = useQuery(
    [RewardAPRQueryKey, network.networkId, asset, address],
    async () => {
      if (asset !== EVestaAsset.GMX && asset !== EVestaAsset.sGLP) return { apr: 0, vestaFee: 0 };

      try {
        const [rewardRate, totalSupply, treasuryFee] = await Promise.all([
          feeTrackerRead.tokensPerInterval(),
          feeTrackerRead.totalSupply(),
          vaultRewardsRead.treasuryFee(),
        ]);

        const vestaFee = 1 - Number(treasuryFee) / 100 / 100;
        const tvl = prices.oracle[computedAsset] * Number(formatEther(totalSupply));
        const distributedTokensAmount = 86400 * 365 * Number(formatEther(rewardRate)) * prices.oracle.ETH;
        const apr = distributedTokensAmount / tvl;

        return {
          apr,
          vestaFee,
        };
      } catch (err) {
        console.error(err);
        throw new Error('Couldnt get Vault APR');
      }
    },
    queryOptions(
      false,
      pricesFetched &&
        !isPricesLoading &&
        checkValidContractAddress([feeTrackerAddress, vaultRewardsAddress]),
    ),
  );

  const boostApr = (res.data?.apr ?? 0) * (asset === EVestaAsset.GMX ? bonusApr.data : 0);
  const realApr = res.data ? (res.data.apr + boostApr) * res.data.vestaFee : 0;

  return {
    ...res,
    data: realApr,
  };
};
